import React, { useCallback, useRef, useState } from "react";
import Popover from "../Popover";
import { Button } from "../Widgets";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import styles from "./DnDReorderTable.module.scss";
import _ from "lodash";
import { CloseMonochromeIconsIcon as CloseXIcon } from "@netapp/bxp-design-system-react/icons/monochrome";

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const calculatePlaceholder = ({ draggableId, destinationIndex, sourceIndex, containerRef }) => {
    const domQuery = `[data-rbd-drag-handle-draggable-id='${draggableId}']`;
    const draggedDOM = document.querySelector(domQuery);

    if (!draggedDOM) {
        return;
    }
    const { clientHeight, clientWidth } = draggedDOM;

    const clientY = [...containerRef.current.children[0].children]
        .slice(0, destinationIndex)
        .reduce((total, curr) => {
            const style = curr.currentStyle || window.getComputedStyle(curr);
            const marginBottom = parseFloat(style.marginBottom);
            return total + curr.clientHeight + marginBottom;
        }, 0);

    return {
        clientHeight,
        clientWidth,
        clientY,
        destinationIndex,
        sourceIndex
    }
}

const calculateShownIndex = ({ index, sourceIndex, destinationIndex, outOfBoundsIndex }) => {
    if (!_.isNull(outOfBoundsIndex)) {
        if (outOfBoundsIndex <= index) {
            return index;
        } else {
            return index + 1;
        }
    } else if (sourceIndex === destinationIndex) {
        return index + 1;
    } else if (destinationIndex < sourceIndex) {
        if (index < destinationIndex) {
            return index + 1;
        } else if (index > sourceIndex) {
            return index + 1;
        } else {
            return index + 2;
        }
    } else {
        if (index < sourceIndex) {
            return index + 1;
        } else if (index <= destinationIndex) {
            return index;
        } else {
            return index + 1;
        }
    }
}

const DefaultCellRenderer = ({ value }) => {
    return <>
        {value}
    </>
}

const RenderHeaderCell = ({ column }) => {
    return <div>
        <div className={styles['header-column-seperator']}></div>
        <div className={styles['header-cell']}>
        {column.popoverText && <Popover triggerClassName={styles['header-cell-popover-trigger']} triggerOnClick white {...column.popoverProps}>{column.popoverText}</Popover>}
        <span>
            {column.header}
        </span>
        </div>
    </div>
}

const RowContentsRenderer = ({ row, columns, rows, index }) => {
    return <>
        {columns.map(column => {
            const Renderer = column.Renderer || DefaultCellRenderer;
            const value = _.isString(row) ? row : (column.accessor ? _.get(row, column.accessor) : row[column.id]);
            return <div key={column.id}>
                <Renderer value={value} row={row} index={index} rows={rows}/>
            </div>
        })}
    </>
}

/**
 * @deprecated - DONT USE IN NEW FEATURES
 */
const DnDReorderTable = React.memo(({ rows, columns, updateRows, minRows = 0 }) => {
    const containerRef = useRef(null);
    const [placeholderProps, setPlaceholderProps] = useState(null);
    const [outOfBoundsIndex, setOutOfBoundsIndex] = useState(null);

    const onDragStart = useCallback(update => {
        const draggableId = update.draggableId;
        const sourceIndex = update.source.index;

        setOutOfBoundsIndex(null);
        setPlaceholderProps(calculatePlaceholder({ draggableId, destinationIndex: sourceIndex, sourceIndex, containerRef }));
    }, [setPlaceholderProps]);

    const onDragUpdate = useCallback(update => {
        if (!update.destination) {
            setPlaceholderProps(null);
            setOutOfBoundsIndex(update.source.index);
            return;
        }
        const draggableId = update.draggableId;
        const destinationIndex = update.destination.index;
        const sourceIndex = update.source.index;

        setOutOfBoundsIndex(null);
        setPlaceholderProps(calculatePlaceholder({ draggableId, destinationIndex, sourceIndex, containerRef }));
    }, [setPlaceholderProps]);

    const onDragEnd = useCallback(result => {
        setPlaceholderProps(null)
        setOutOfBoundsIndex(null);

        if (!result.destination) {
            return;
        }

        updateRows(reorder(rows, result.source.index, result.destination.index));
    }, [setPlaceholderProps, updateRows, rows])

    return <div className={styles.container}>
        <div style={{ gridTemplateColumns: `24px repeat(${columns.length}, 1fr) 52px` }} className={styles['header-row']}>
            <div>#</div>
            {
                columns.map(column => {
                    return <RenderHeaderCell column={column} key={column.id}/>
                })
            }
            <div/>
        </div>
        <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate} onDragStart={onDragStart}>
            <div ref={containerRef} style={{ position: "relative" }}>
                <Droppable droppableId="export-policy" renderClone={(
                    provided,
                    snapshot,
                    rubric
                ) => {
                    return <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                style={{ ...provided.draggableProps.style, zIndex: 5000000000, gridTemplateColumns: `24px repeat(${columns.length}, 1fr) 52px` }} className={styles.row}>
                        <div style={{ opacity: 0 }}/>
                        <RowContentsRenderer columns={columns} rows={rows} row={rows[rubric.source.index]} index={rubric.source.index}/>
                        <div>
                            <Button type="icon" color="dark-grey"><CloseXIcon/></Button>
                        </div>
                    </div>
                }}>
                    {(droppableProvided) => (
                        <div
                            ref={ref => droppableProvided.innerRef(ref)}
                            {...droppableProvided.droppableProps}
                        >
                            {rows.map((row, index) => (
                                <Draggable
                                    draggableId={index.toString()}
                                    index={index}
                                    key={index}
                                >
                                    {(
                                        provided,
                                    ) => {

                                        return <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                                    style={{ ...provided.draggableProps.style, gridTemplateColumns: `24px repeat(${columns.length}, 1fr) 52px` }} className={styles.row}>
                                            <div>
                                                {calculateShownIndex({ index, sourceIndex: placeholderProps?.sourceIndex, destinationIndex: placeholderProps?.destinationIndex, outOfBoundsIndex })}
                                            </div>
                                            <RowContentsRenderer columns={columns} row={row} rows={rows} index={index}/>
                                            <div>
                                                <Button type="icon" color="dark-grey" onClick={() => updateRows([...rows.slice(0, index), ...rows.slice(index + 1)])} disabled={rows.length <= minRows}><CloseXIcon/></Button>
                                            </div>
                                        </div>
                                    }}
                                </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                            {placeholderProps && <div style={{
                                top: placeholderProps.clientY,
                                height: placeholderProps.clientHeight,
                                width: placeholderProps.clientWidth
                            }} className={styles.placeholder}>
                                <div>
                                    {placeholderProps.destinationIndex + 1}
                                </div>
                                <div/>
                            </div>}
                        </div>
                    )}
                </Droppable>
            </div>
        </DragDropContext>
    </div>
});

export default DnDReorderTable;